// @LTG-ID: F-003
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-003, source Landing Page.html lines 1302-1400
// @CHANGED: 2026-02 session_002
// @OWNER: web

import { useEffect, useRef, useState } from "react";
import ExternalCTA from "../../lib/external-cta";
import "./hero.css";

const CONVO_SCRIPT = [
    {
        role: "advisor",
        text: (
            <>
                Hi, I'm <strong>your KGOB advisor</strong>. What brings you in
                today?
            </>
        ),
        delay: 600,
    },
    {
        role: "owner",
        text: "We're 18 months out from a sale. I have no idea what we're actually worth.",
        delay: 2200,
    },
    {
        role: "advisor",
        text: (
            <>
                Got it. Three quick numbers, then we'll know more in 6 minutes
                than most owners learn in years.
            </>
        ),
        delay: 2400,
    },
    {
        role: "owner",
        text: "Okay. Revenue $7.4M. Margin around 22%. Construction-adjacent services.",
        delay: 2400,
    },
    { role: "typing", text: null, delay: 2000 },
    {
        role: "advisor",
        text: (
            <>
                Median multiple in your sector ran{" "}
                <strong>3.4x to 4.1x EBITDA</strong> last quarter. We would
                start your range at <strong>$5.5M to $6.7M</strong>, then look
                at five drivers that move it.
            </>
        ),
        delay: 2600,
    },
];

export function Hero() {
    const [shown, setShown] = useState(0);
    const listRef = useRef(null);

    // Advance through the script.
    useEffect(() => {
        if (shown >= CONVO_SCRIPT.length) return undefined;
        const t = window.setTimeout(
            () => setShown((s) => s + 1),
            CONVO_SCRIPT[shown]?.delay || 1500
        );
        return () => window.clearTimeout(t);
    }, [shown]);

    // Auto-scroll the message list to the bottom on each new message.
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [shown]);

    // Loop: when fully shown, wait then restart.
    useEffect(() => {
        if (shown !== CONVO_SCRIPT.length) return undefined;
        const t = window.setTimeout(() => setShown(0), 6000);
        return () => window.clearTimeout(t);
    }, [shown]);

    return (
        <section className="ltg-hero" id="top" data-testid="hero">
            <div className="ltg-container">
                <div className="ltg-hero__grid">
                    <div>
                        <span className="ltg-eyebrow" data-testid="hero-eyebrow">
                            LET&apos;S TALK GROWTH
                        </span>
                        <h1 className="ltg-hero__h1" data-testid="hero-h1">
                            What is your business <em>actually</em> worth?
                        </h1>
                        <p className="ltg-hero__lede" data-testid="hero-lede">
                            Tax, advisory, business valuation and exit planning
                            for owners who are ready to know what they have
                            built, and what it can become. Real CPAs. Real
                            models. No theatrics.
                        </p>
                        <p
                            className="ltg-hero__offer-line"
                            data-testid="hero-offer-line"
                        >
                            The Strategic Snapshot: a CPA-signed indicative
                            valuation of your business. $995.
                        </p>
                        <div className="ltg-hero__ctas">
                            <ExternalCTA
                                ctaId="hero-primary"
                                className="ltg-btn ltg-btn-primary"
                            >
                                Get Your Valuation{" "}
                                <span className="ltg-arrow" aria-hidden="true">
                                    &rarr;
                                </span>
                            </ExternalCTA>
                            <a
                                href="#snapshot"
                                className="ltg-btn ltg-btn-secondary"
                                data-cta-id="hero-secondary"
                                data-testid="cta-hero-secondary"
                            >
                                Try the free 6-minute preview
                            </a>
                        </div>
                        <div
                            className="ltg-hero__micro-trust"
                            data-testid="hero-micro-trust"
                        >
                            <span>
                                &middot; KGOB methodology, NC CPA #30420
                            </span>
                            {/* CHG-0107.2 — 14-day refund policy chip removed */}
                            <span className="ltg-hero__micro-disclaimer">
                                Indicative valuation. Not a formal appraisal
                                under SSVS No.&nbsp;1.
                            </span>
                        </div>
                        <div
                            className="ltg-hero__meta"
                            data-testid="hero-meta"
                        >
                            <span>
                                <i className="ltg-hero__dot" />
                                Since 1988
                            </span>
                            <span>
                                <i className="ltg-hero__dot" />
                                Offices across NC
                            </span>
                            <span>
                                <i className="ltg-hero__dot" />
                                Clients in all 50 states
                            </span>
                        </div>
                    </div>

                    <div
                        className="ltg-hero__convo"
                        data-testid="hero-convo-card"
                    >
                        <div className="ltg-hero__convo-head">
                            <div className="ltg-hero__convo-head-l">
                                <span
                                    className="ltg-hero__live-dot"
                                    aria-hidden="true"
                                />
                                <h3 className="ltg-hero__convo-title">
                                    What a first call sounds like
                                </h3>
                            </div>
                            <span className="ltg-hero__convo-head-r">
                                Live preview
                            </span>
                        </div>
                        <div
                            className="ltg-hero__msg-list"
                            ref={listRef}
                            aria-live="polite"
                            data-testid="hero-convo-list"
                        >
                            {CONVO_SCRIPT.slice(0, shown).map((m, i) => {
                                if (m.role === "typing") {
                                    return (
                                        <div
                                            key={`msg-${i}`}
                                            className="ltg-hero__msg ltg-hero__msg--advisor"
                                        >
                                            <div className="ltg-hero__msg-avatar">
                                                A
                                            </div>
                                            <div className="ltg-hero__typing">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={`msg-${i}`}
                                        className={`ltg-hero__msg ltg-hero__msg--${m.role}`}
                                    >
                                        <div className="ltg-hero__msg-avatar">
                                            {m.role === "owner" ? "You" : "A"}
                                        </div>
                                        <div className="ltg-hero__msg-bubble">
                                            {m.text}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Hero;
